import React from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { BookContextItem } from '../../hooks/useBookContext';

interface BookContextPillsProps {
  books: BookContextItem[];
  onRemove: (bookId: string) => void;
  /** When provided, renders an "Add a book" CTA pill when no book is
   *  tagged. General chat requires exactly one tagged book; this gives
   *  the user a clear entry point. Omit on flows where book context
   *  is optional (e.g. book-pinned sessions). */
  onAddClick?: () => void;
}

export const BookContextPills: React.FC<BookContextPillsProps> = ({
  books,
  onRemove,
  onAddClick,
}) => {
  if (books.length === 0) {
    // Empty state — show "Tag a book" CTA pill if the caller provided
    // a click handler. Otherwise render nothing (the original behavior).
    if (!onAddClick) return null;
    return (
      <div className="mb-2 px-1">
        <button
          type="button"
          onClick={onAddClick}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-dashed border-amber-400 dark:border-amber-700 text-amber-700 dark:text-amber-300 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
          aria-label="Tag a book to chat about"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          <span>Tag a book to start the chat</span>
        </button>
      </div>
    );
  }

  return (
    <div className="mb-2 px-1">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-gray-500 dark:text-gray-500">📚</span>
        {books.map(book => (
          <div
            key={book.id}
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-xs"
          >
            <span className="font-medium truncate max-w-[120px]">{book.title}</span>
            <button
              onClick={() => onRemove(book.id)}
              className="hover:bg-blue-100 dark:hover:bg-blue-800 rounded p-0.5 transition-colors flex-shrink-0"
              aria-label={`Remove ${book.title}`}
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
